import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    Paper,
    Alert,
    CircularProgress,
    Divider,
    Chip,
} from "@mui/material";
import { analyzeTemplateParameters } from "../../../utils/templateParameterUtils";
import { WB_SX } from "../../../ui/ux/uiTokens";

const ParameterInsertionStep = ({
    selectedTemplate,
    templateDetails,
    loadingTemplateDetails,
    parameters,
    onParametersChange,
}) => {
    const [localParameters, setLocalParameters] = useState(parameters || {});
    
    // Analyze template parameters using utility function
    const paramAnalysis = useMemo(() => {
        if (!templateDetails) return null;
        return analyzeTemplateParameters(templateDetails);
    }, [templateDetails]);

    useEffect(() => {
        if (paramAnalysis && !parameters) {
            // Initialize parameters based on template analysis
            const newParams = {
                header: [],
                body: [],
                buttons: {},
            };

            // Initialize header parameters
            if (paramAnalysis.header.hasParameters) {
                newParams.header = new Array(paramAnalysis.header.count).fill("");
            }

            // Initialize body parameters
            if (paramAnalysis.body.hasParameters) {
                newParams.body = new Array(paramAnalysis.body.count).fill("");
            }

            // Initialize button parameters (for URL buttons with parameters)
            if (paramAnalysis.buttons.hasParameters && paramAnalysis.buttons.buttons) {
                paramAnalysis.buttons.buttons.forEach((button, index) => {
                    if (button.type === "URL" && button.url) {
                        // Extract parameter count from button URL
                        const urlMatches = button.url.match(/\{\{(\d+)\}\}/g) || [];
                        if (urlMatches.length > 0) {
                            newParams.buttons[`button_${index}`] = {
                                buttonIndex: index,
                                buttonText: button.text || `Button ${index + 1}`,
                                parameters: new Array(urlMatches.length).fill(""),
                            };
                        }
                    }
                });
            }

            setLocalParameters(newParams);
            onParametersChange(newParams);
        }
    }, [paramAnalysis]);

    useEffect(() => {
        if (parameters) {
            setLocalParameters(parameters);
        }
    }, [parameters]);

    if (!selectedTemplate) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">
                    Please select a template in Step 1 first.
                </Alert>
            </Box>
        );
    }

    if (loadingTemplateDetails || !templateDetails) {
        return (
            <Box
                sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    {loadingTemplateDetails
                        ? "Loading template details..."
                        : "Template details not available"}
                </Typography>
            </Box>
        );
    }

    if (!paramAnalysis) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="warning">
                    Unable to analyze template parameters.
                </Alert>
            </Box>
        );
    }

    const hasHeaderParams = paramAnalysis.header.hasParameters;
    const hasBodyParams = paramAnalysis.body.hasParameters;
    const hasButtonParams = paramAnalysis.buttons.hasParameters;
    const hasAnyParams = hasHeaderParams || hasBodyParams || hasButtonParams;

    const handleParameterChange = (type, index, value) => {
        const newParams = {
            ...localParameters,
            [type]: [...(localParameters[type] || [])],
        };
        newParams[type][index] = value;
        setLocalParameters(newParams);
        onParametersChange(newParams);
    };

    const handleButtonParameterChange = (buttonKey, paramIndex, value) => {
        const newParams = {
            ...localParameters,
            buttons: {
                ...(localParameters.buttons || {}),
                [buttonKey]: {
                    ...(localParameters.buttons?.[buttonKey] || {}),
                    parameters: [
                        ...(localParameters.buttons?.[buttonKey]?.parameters || []),
                    ],
                },
            },
        };
        newParams.buttons[buttonKey].parameters[paramIndex] = value;
        setLocalParameters(newParams);
        onParametersChange(newParams);
    };

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                Insert Template Parameters
            </Typography>

            {!hasAnyParams && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    This template does not require any parameters. You can proceed
                    to the next step.
                </Alert>
            )}

            {/* Header Parameters */}
            {hasHeaderParams && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Typography variant="subtitle1" sx={WB_SX.sectionTitle}>
                            Header Parameters
                        </Typography>
                        <Chip
                            label={`${paramAnalysis.header.parameterType} - ${paramAnalysis.header.count} parameter(s)`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                    {localParameters.header?.map((param, index) => {
                        // Use appropriate input type based on parameter type
                        const isURL = paramAnalysis.header.parameterType === "URL";
                        return (
                            <TextField
                                key={`header-${index}`}
                                fullWidth
                                label={`Header Parameter ${index + 1} ({{${index + 1}}})`}
                                value={param || ""}
                                onChange={(e) =>
                                    handleParameterChange("header", index, e.target.value)
                                }
                                sx={{ mb: 2 }}
                                required
                                type={isURL ? "url" : "text"}
                                helperText={
                                    isURL
                                        ? `Enter URL value for parameter {{${index + 1}}} (e.g., https://example.com)`
                                        : `Enter text value for parameter {{${index + 1}}} in header`
                                }
                                placeholder={isURL ? "https://example.com" : "Enter text here"}
                            />
                        );
                    })}
                </Paper>
            )}

            {/* Body Parameters */}
            {hasBodyParams && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Typography variant="subtitle1" sx={WB_SX.sectionTitle}>
                            Body Parameters
                        </Typography>
                        <Chip
                            label={`${paramAnalysis.body.parameterType} - ${paramAnalysis.body.count} parameter(s)`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                    {localParameters.body?.map((param, index) => {
                        // Calculate the actual parameter number (considering header params)
                        const paramNumber = (paramAnalysis.header.count || 0) + index + 1;
                        const isURL = paramAnalysis.body.parameterType === "URL";
                        
                        return (
                            <TextField
                                key={`body-${index}`}
                                fullWidth
                                label={`Body Parameter ${index + 1} ({{${paramNumber}}})`}
                                value={param || ""}
                                onChange={(e) =>
                                    handleParameterChange("body", index, e.target.value)
                                }
                                sx={{ mb: 2 }}
                                required
                                type={isURL ? "url" : "text"}
                                helperText={
                                    isURL
                                        ? `Enter URL value for parameter {{${paramNumber}}} (e.g., https://example.com)`
                                        : `Enter text value for parameter {{${paramNumber}}} in body`
                                }
                                placeholder={isURL ? "https://example.com" : "Enter text here"}
                                multiline={!isURL}
                                rows={!isURL ? 2 : 1}
                            />
                        );
                    })}
                </Paper>
            )}

            {/* Button Parameters */}
            {hasButtonParams && localParameters.buttons && (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Typography variant="subtitle1" sx={WB_SX.sectionTitle}>
                            Button URL Parameters
                        </Typography>
                        <Chip
                            label={`${paramAnalysis.buttons.parameterType} - ${paramAnalysis.buttons.count} parameter(s)`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                    {Object.keys(localParameters.buttons).map((buttonKey) => {
                        const buttonData = localParameters.buttons[buttonKey];
                        if (!buttonData || !buttonData.parameters) return null;

                        return (
                            <Box key={buttonKey} sx={{ mb: 3 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ ...WB_SX.sectionTitle, mb: 1, color: "text.secondary" }}
                                >
                                    {buttonData.buttonText}
                                </Typography>
                                {buttonData.parameters.map((param, paramIndex) => {
                                    // Button parameters are always URL type
                                    return (
                                        <TextField
                                            key={`${buttonKey}-param-${paramIndex}`}
                                            fullWidth
                                            label={`URL Parameter ${paramIndex + 1}`}
                                            value={param || ""}
                                            onChange={(e) =>
                                                handleButtonParameterChange(
                                                    buttonKey,
                                                    paramIndex,
                                                    e.target.value
                                                )
                                            }
                                            sx={{ mb: 2 }}
                                            required
                                            type="url"
                                            helperText={`Enter URL value for parameter ${paramIndex + 1} (e.g., https://example.com)`}
                                            placeholder="https://example.com"
                                        />
                                    );
                                })}
                                {Object.keys(localParameters.buttons).length > 1 && (
                                    <Divider sx={{ mt: 2 }} />
                                )}
                            </Box>
                        );
                    })}
                </Paper>
            )}
        </Box>
    );
};

export default ParameterInsertionStep;

